import {
	Component,
	OnInit,
	Input,
	EventEmitter,
	Output,
	ChangeDetectorRef,
	AfterViewInit,
	signal,
	WritableSignal,
	OnChanges,
	DestroyRef,
	input,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { DatePipe, NgClass } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import {
	MAT_DATEPICKER_SCROLL_STRATEGY,
	MatDatepickerInputEvent,
	MatDatepickerModule,
} from '@angular/material/datepicker';
import { MatMenuModule } from '@angular/material/menu';
import { Overlay } from '@angular/cdk/overlay';
import { MAT_DATE_FORMATS, MAT_NATIVE_DATE_FORMATS } from '@angular/material/core';
import { AuUtilsFunctions } from '@app/shared';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgxTolgeeModule } from '@tolgee/ngx';

const DATE_FORMATS = {
	...MAT_NATIVE_DATE_FORMATS,
	display: {
		...MAT_NATIVE_DATE_FORMATS.display,
		dateInput: {
			year: 'numeric',
			month: 'short',
			day: '2-digit',
		} as Intl.DateTimeFormatOptions,
	},
};

@Component({
	selector: 'cmms-date-picker',
	templateUrl: './cmms-date-picker.component.html',
	styleUrls: ['./cmms-date-picker.component.scss'],
	imports: [
		MatInputModule,
		MatDatepickerModule,
		MatMenuModule,
		DatePipe,
		ReactiveFormsModule,
		NgClass,
		NgxTolgeeModule,
	],
	providers: [
		{
			provide: MAT_DATEPICKER_SCROLL_STRATEGY,
			useFactory: (overlay: Overlay) => () => overlay.scrollStrategies.noop(),
			deps: [Overlay],
		},
		{ provide: MAT_DATE_FORMATS, useValue: DATE_FORMATS },
	],
})
export class CmmsDatePickerComponent implements OnInit, AfterViewInit, OnChanges {
	@Input() set restrictPastSelections(restrict: boolean) {
		if (restrict) {
			this.minDate = new Date();
		}
	}
	@Input() date: Date;
	@Input() set value(value: string | Date) {
		if (value) {
			this.formControl.patchValue(new Date(value));
			if (this.checkOutdated) {
				this.checkForOutdated(value);
			}
		}
	}
	@Input() label = 'Date';
	@Input() placeholderText = 'Select Date';
	@Input() isInputBased = false;
	@Input() checkOutdated = false;

	disableFutureDates = input(false);
	now = signal(new Date());

	@Output() dateTimeChanged = new EventEmitter<Date | string>();
	@Output() dateChanged = new EventEmitter<Date | string>();
	@Output() controlReady = new EventEmitter<FormControl>();

	formControl = new FormControl(null);
	minDate: Date;
	outDated: WritableSignal<boolean> = signal(false);

	constructor(
		private cdr: ChangeDetectorRef,
		private datePipe: DatePipe,
		private destroyRef: DestroyRef
	) {}

	selectedChange($event: Date) {
		this.date = $event;
		this.dateTimeChanged.emit(this.convertDateToISOStringWithoutTimezone(this.date));
	}

	ngOnInit(): void {
		this.controlReady.emit(this.formControl);

		if (this.isInputBased && this.checkOutdated) {
			this.formControl.valueChanges
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe(value => {
					this.checkForOutdated(value);
				});
		}
	}

	ngAfterViewInit() {
		this.formControl.valueChanges.subscribe(value => {
			if (value) {
				this.date = value;
				this.cdr.detectChanges();
			}
		});
	}
	ngOnChanges() {
		if (!this.isInputBased && this.checkOutdated) {
			this.checkForOutdated(this.date);
		}
	}

	onDateChange($event: MatDatepickerInputEvent<any, any>): void {
		this.dateChanged.emit(this.datePipe.transform($event.value, 'yyyy-MM-dd'));
	}

	convertDateToISOStringWithoutTimezone(date: Date): string {
		return !date ? null : this.datePipe.transform(date, 'yyyy-MM-ddT00:00:00');
	}

	convertIntoDateForMatCalendar(dateStr) {
		return new Date(dateStr);
	}

	private checkForOutdated(date: string | Date) {
		if (AuUtilsFunctions.isDateBeforeToday(new Date(date))) {
			this.outDated.set(true);
		} else {
			this.outDated.set(false);
		}
	}
}
